// Company → Operations → Licenses (N-095). Owner, 2026-09-10: "Licenses
// needs to be a table, just like other things" / "i need these put in a
// table that lays out what it is, year and expiration date and link to its
// location in drive not just a drive folder link."
//
// Replaces the old read-only LicensesTable.tsx that lived under Documents →
// Licenses disk: this is a first-class table, alongside Vendors and SPC,
// with manual add/edit/delete on top of the rows N-094's forward-and-file
// classifier already writes. Clinic-filtered client side from the one
// cached fetch (LICENSES_KEY), exactly like ServiceCompliance.tsx — same
// role gate and form pattern as CompanyVendors.tsx (owner/director
// everywhere, a manager on their own clinic, never while viewing-as).
//
// This app never creates Drive folders or files (owner rule, D-60): the file
// picker only lists what is already in the clinic's Licenses Drive folder
// (fetched the same way CompanyDrivePane does), or accepts a pasted Drive
// link with the file id pulled out of it.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { FavoriteStar } from './FavoriteStar';
import { OpenFolderLink } from './OpenFolderLink';
import { useWrite } from './MoneyForms';
import { useResource } from '../lib/store';
import { session } from '../lib/session';
import { clinicToday } from '../lib/clinicDay';
import { isoToMd } from '../lib/serviceCompliance';
import { companyDriveKey, fetchCompanyDrivePane, refreshCompanyDriveLists } from '../lib/companyDrive';
import {
  LICENSES_KEY, fetchLicenses, daysUntilExpiry, isExpiringSoon, licenseYear,
  extractDriveFileId, addLicense, updateLicense, deleteLicense, reloadLicenses,
  scanLicenses, moveLicenseToOld, isOldLicense, LICENSE_ATTENTION_DAYS,
  type LicenseRow, type LicensesData,
} from '../lib/licenses';

const EM_DASH = '—';

function canWriteLicenses(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR' || me.role === 'MANAGER'));
}

/**
 * N-099 (owner, 2026-09-10): scanning a clinic's whole Licenses folder and
 * calling the paid classifier on every file is a bigger, blunter action than
 * adding one license by hand — OWNER/DIRECTOR only, narrower than
 * canWriteLicenses above, matching the server gate
 * (src/intake_routes.js's isOwnerOrDirector on scanCompanyLicensesFolder).
 */
function canScanLicenses(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

/**
 * N-100 (owner, 2026-09-10, verbatim): "then i need a button to 'move to
 * old licenses'. only for myself and Candace" — Candace is the DIRECTOR.
 * Same rule as canScanLicenses, named separately in case the two ever
 * diverge; matches the server gate on moveCompanyLicenseToOld /
 * ensureCompanyOldLicensesFolder.
 */
function canMoveToOld(): boolean {
  return canScanLicenses();
}

function timing(days: number | null): string {
  if (days == null) return '';
  if (days < 0) return `overdue ${-days} day${-days === 1 ? '' : 's'}`;
  if (days === 0) return 'due today';
  return `in ${days} days`;
}

function sortByExpiry(rows: LicenseRow[]): LicenseRow[] {
  return [...rows].sort((a, b) => {
    if (!a.expiresOn && !b.expiresOn) return 0;
    if (!a.expiresOn) return 1;
    if (!b.expiresOn) return -1;
    return a.expiresOn.localeCompare(b.expiresOn);
  });
}

function blank(clinic: string) {
  return {
    id: 0,
    kind: '',
    issuer: '',
    number: '',
    issuedOn: '',
    expiresOn: '',
    holder: '',
    driveFileId: '',
    linkInput: '',
    locationCode: clinic,
  };
}

interface DriveFileOption {
  id: string;
  name: string;
  // N-100: the picker and the Old Licenses folder lookup both need to tell
  // a real file apart from a Drive folder entry.
  mimeType?: string;
}

interface DriveSectionData {
  name: string;
  files?: DriveFileOption[];
  folderUrl?: string;
}

const DRIVE_FOLDER_MIME = 'application/vnd.google-apps.folder';

interface DrivePaneData {
  sections?: DriveSectionData[];
}

export function CompanyLicenses({ clinic, pinKey }: { clinic: string; pinKey?: string }): JSX.Element {
  const data = useResource<LicensesData>(LICENSES_KEY, fetchLicenses);
  const drive = useResource<DrivePaneData>(
    companyDriveKey('documents', clinic),
    () => fetchCompanyDrivePane('documents', clinic) as Promise<DrivePaneData>,
  );
  const save = useWrite();
  const scan = useWrite();
  const [form, setForm] = useState(() => blank(clinic));
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [oldOpen, setOldOpen] = useState(false);
  const today = clinicToday();
  const canWrite = canWriteLicenses();
  const canScan = canScanLicenses();
  const canMove = canMoveToOld();

  async function runScan(): Promise<void> {
    setScanResult(null);
    await scan.run(async () => {
      const result = await scanLicenses(clinic);
      setScanResult(`Added ${result.added}, already listed ${result.alreadyListed}, skipped ${result.skippedUnreadable}`);
      await reloadLicenses();
    });
  }

  if (data.status === 'loading') return <EmptyState label="Loading licenses…" />;
  if (data.status === 'fault') return <FaultState name="licenses_load_failed" detail={data.error} />;

  const clinicRows = (data.data.rows ?? []).filter((r) => r.locationCode === clinic);
  const rows = sortByExpiry(clinicRows.filter((r) => !isOldLicense(r)));
  const oldRows = sortByExpiry(clinicRows.filter(isOldLicense));
  const licensesSection = drive.status === 'ready'
    ? (drive.data.sections ?? []).find((s) => s.name === 'Licenses')
    : undefined;
  const licensesFiles: DriveFileOption[] = licensesSection?.files ?? [];
  // The manual-add picker only ever offers a real file — "Old Licenses"
  // (or any other Drive folder sitting in the same listing) must never show
  // up as something you could file a license as (N-100).
  const driveFiles: DriveFileOption[] = licensesFiles.filter((f) => f.mimeType !== DRIVE_FOLDER_MIME);

  function patch(next: Partial<typeof form>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  function startEdit(row: LicenseRow): void {
    setForm({
      id: row.id,
      kind: row.kind,
      issuer: row.issuer || '',
      number: row.number || '',
      issuedOn: row.issuedOn || '',
      expiresOn: row.expiresOn || '',
      holder: row.holder || '',
      driveFileId: row.driveFileId || '',
      linkInput: '',
      locationCode: clinic,
    });
  }

  function resolveDriveFileId(): { ok: true; id: string } | { ok: false; error: string } {
    const pasted = form.linkInput.trim();
    if (pasted) {
      const id = extractDriveFileId(pasted);
      if (!id) return { ok: false, error: "that doesn't look like a Drive file link — paste the file's own link, not a folder" };
      return { ok: true, id };
    }
    return { ok: true, id: form.driveFileId };
  }

  async function submit(): Promise<void> {
    await save.run(async () => {
      const resolved = resolveDriveFileId();
      if (!resolved.ok) throw new Error(resolved.error);
      const body = {
        locationCode: clinic,
        kind: form.kind.trim(),
        issuer: form.issuer.trim(),
        number: form.number.trim(),
        issuedOn: form.issuedOn,
        expiresOn: form.expiresOn,
        holder: form.holder.trim(),
        driveFileId: resolved.id,
      };
      if (form.id) await updateLicense(form.id, body);
      else await addLicense(body);
      setForm(blank(clinic));
      await reloadLicenses();
    });
  }

  // N-100: "Remove from table" — the server-side write was always a soft
  // delete (the Drive file is never touched); only the button's own words
  // changed, per the owner's "i am not sure i want things to be deleted
  // rather removed from a table."
  async function remove(row: LicenseRow): Promise<void> {
    await save.run(async () => {
      await deleteLicense(row.id);
      if (form.id === row.id) setForm(blank(clinic));
      await reloadLicenses();
    });
  }

  // N-100: moves the Drive file into the clinic's Old Licenses folder (server
  // side) and reloads both this table and the Documents pane so the file
  // listing follows the move. Shares `save` with the form above — one error
  // line for every write this card can make.
  async function moveToOld(row: LicenseRow): Promise<void> {
    await save.run(async () => {
      await moveLicenseToOld(row.id);
      await reloadLicenses();
      refreshCompanyDriveLists([companyDriveKey('documents', clinic)]);
    });
  }

  return (
    <div class="card" data-testid="company-licenses">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Licenses</h2>
          {/* Owner, 2026-09-10: the pin lives inside the table, not in the menu row.
              "keep 'Open folder' in the table header" — the Drive folder card
              that used to sit under Documents → Licenses is gone (N-100); this
              is the only way left to open the clinic's Licenses folder. */}
          <div class="section-head-actions">
            <OpenFolderLink url={licensesSection?.folderUrl} label="Licenses" />
            {canScan ? (
              <button
                type="button"
                class="btn"
                data-testid="scan-licenses-folder"
                disabled={scan.busy}
                onClick={() => { void runScan(); }}
              >
                {scan.busy ? 'Scanning…' : 'Scan the Licenses folder'}
              </button>
            ) : null}
            {pinKey ? <FavoriteStar itemKey={pinKey} /> : null}
          </div>
        </div>
        <p>What it is, the year, when it expires, and a link to the file in Drive. Green is valid, orange expires within 90 days, red has expired.</p>
        {scanResult ? <p class="hint" data-testid="scan-licenses-result">{scanResult}</p> : null}
        {scan.error ? <p class="bad" role="alert">{scan.error}</p> : null}
      </div>

      {canWrite ? (
        <form
          class="service-point-form"
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
        >
          <label>
            License
            <input
              required
              value={form.kind}
              onInput={(e) => patch({ kind: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Issuer
            <input value={form.issuer} onInput={(e) => patch({ issuer: (e.target as HTMLInputElement).value })} />
          </label>
          <label>
            Number
            <input value={form.number} onInput={(e) => patch({ number: (e.target as HTMLInputElement).value })} />
          </label>
          <label>
            Issued On
            <input type="date" value={form.issuedOn} onInput={(e) => patch({ issuedOn: (e.target as HTMLInputElement).value })} />
          </label>
          <label>
            Expires On
            <input type="date" value={form.expiresOn} onInput={(e) => patch({ expiresOn: (e.target as HTMLInputElement).value })} />
          </label>
          <label>
            Holder
            <input value={form.holder} onInput={(e) => patch({ holder: (e.target as HTMLInputElement).value })} />
          </label>
          <label>
            File already in the Licenses folder
            <select
              aria-label="File already in the Licenses folder"
              value={form.driveFileId}
              onChange={(e) => patch({ driveFileId: (e.target as HTMLSelectElement).value, linkInput: '' })}
            >
              <option value="">— none —</option>
              {driveFiles.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </label>
          <label>
            Or paste a Drive file link
            <input
              placeholder="https://drive.google.com/file/d/…"
              value={form.linkInput}
              onInput={(e) => patch({ linkInput: (e.target as HTMLInputElement).value, driveFileId: '' })}
            />
          </label>
          <div class="service-point-actions">
            <button type="submit" class="btn primary" disabled={save.busy || !form.kind.trim()}>
              {form.id ? 'Save' : 'Add'}
            </button>
            {form.id ? (
              <button type="button" class="btn" disabled={save.busy} onClick={() => setForm(blank(clinic))}>
                Cancel
              </button>
            ) : null}
          </div>
          {save.error ? <p class="bad" role="alert">{save.error}</p> : null}
        </form>
      ) : null}

      {rows.length === 0 ? (
        <p class="empty">No licenses on file for this clinic yet.</p>
      ) : (
        <div class="table-wrap">
          <table class="data" data-testid="licenses-table">
            <thead>
              <tr>
                <th>License</th>
                <th>Issuer</th>
                <th>Number</th>
                <th>Year</th>
                <th>Expires</th>
                <th>File</th>
                <th>Status</th>
                {canWrite ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const days = daysUntilExpiry(r, today);
                // Owner, 2026-09-10: "active valid licenses to be in light green
                // highlight, those that expire within 90 days light orange and
                // those that expired, in light red." A row with no expiry date
                // gets no colour — nothing is known about it.
                const overdue = days != null && days < 0;
                const soon = !overdue && isExpiringSoon(r, today, LICENSE_ATTENTION_DAYS);
                const valid = days != null && !overdue && !soon;
                return (
                  <tr
                    key={r.id}
                    class={overdue ? 'license-expired' : soon ? 'license-due' : valid ? 'license-valid' : undefined}
                    data-due={soon ? 'true' : 'false'}
                    data-testid={`license-row-${r.id}`}
                  >
                    <td>{r.kind}</td>
                    <td>{r.issuer || EM_DASH}</td>
                    <td>{r.number || EM_DASH}</td>
                    <td>{licenseYear(r)}</td>
                    <td>
                      {isoToMd(r.expiresOn)}
                      {soon || overdue ? <div>{timing(days)}</div> : null}
                    </td>
                    <td>
                      {r.driveUrl ? <a href={r.driveUrl} target="_blank" rel="noreferrer">Open file</a> : 'no file yet'}
                    </td>
                    <td>
                      <span class={r.status === 'NEEDS_REVIEW' ? 'badge warn' : 'badge'}>
                        {r.status === 'NEEDS_REVIEW' ? 'Needs review' : 'Filed'}
                      </span>
                    </td>
                    {canWrite ? (
                      <td>
                        <button type="button" class="btn" disabled={save.busy} onClick={() => startEdit(r)}>Edit</button>
                        {canMove ? (
                          <button
                            type="button"
                            class="btn"
                            data-testid={`move-to-old-${r.id}`}
                            disabled={save.busy}
                            onClick={() => { void moveToOld(r); }}
                          >
                            Move to Old Licenses
                          </button>
                        ) : null}
                        <button type="button" class="btn" disabled={save.busy} onClick={() => { void remove(r); }}>Remove from table</button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* N-100 (owner, 2026-09-10): "the table ends with 'Old Licenses (N)'
          that expands them, like Inactive Employees" — same closed-by-default
          expander pattern as People.tsx's InactiveEmployeesPanel, except the
          rows are already on hand (one fetch backs both tables), so this is
          just a local toggle over data already loaded. */}
      <div class="old-licenses" data-testid="old-licenses">
        <button
          type="button"
          class="btn"
          data-testid="old-licenses-toggle"
          onClick={() => setOldOpen((v) => !v)}
        >
          {`Old Licenses (${oldRows.length})`}
        </button>
        {oldOpen ? (
          <section class="card" data-testid="old-licenses-table">
            {oldRows.length === 0 ? (
              <p class="empty">Nothing moved to Old Licenses yet.</p>
            ) : (
              <div class="table-wrap">
                <table class="data">
                  <thead>
                    <tr>
                      <th>License</th>
                      <th>Issuer</th>
                      <th>Number</th>
                      <th>Year</th>
                      <th>Expires</th>
                      <th>File</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldRows.map((r) => (
                      <tr key={r.id} data-testid={`old-license-row-${r.id}`}>
                        <td>{r.kind}</td>
                        <td>{r.issuer || EM_DASH}</td>
                        <td>{r.number || EM_DASH}</td>
                        <td>{licenseYear(r)}</td>
                        <td>{isoToMd(r.expiresOn)}</td>
                        <td>
                          {r.driveUrl ? <a href={r.driveUrl} target="_blank" rel="noreferrer">Open file</a> : 'no file yet'}
                        </td>
                        <td>
                          <span class={r.status === 'NEEDS_REVIEW' ? 'badge warn' : 'badge'}>
                            {r.status === 'NEEDS_REVIEW' ? 'Needs review' : 'Filed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
